"use client";

import { ChangeEvent, useCallback, useMemo } from "react";
import { redirect, useSearchParams } from "next/navigation";
import { InputField } from "@stone-payments/jade";
import { storeAnalitica } from "@/app/(store)/_utils/events";
import { useFilters } from "@/contexts/filters-provider";
import { debounce } from "@/utils/debounce";
import { buildQueryString } from "@/utils/url";

export const SearchBar = ({
  size = "medium",
}: {
  size?: "medium" | "large";
}) => {
  const searchParams = useSearchParams();
  const {
    setIsFiltering,
    searchTerm: term,
    setCategoryId,
    setSearchTerm: setTerm,
  } = useFilters();

  const doSearch = useCallback(
    (newTerm: string) => {
      const { queryString } = buildQueryString({
        ...Object.fromEntries(searchParams),
        categoria: null,
        busca: newTerm || null,
      });

      storeAnalitica.events.action.searched();

      setIsFiltering(true);
      redirect(queryString);
    },
    [searchParams, setIsFiltering]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length > 2) doSearch(value);
        else if (value.length === 0) doSearch("");
      }, 300),
    [doSearch]
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;

    setTerm(value);
    setCategoryId("");
    debouncedSearch(value);
  };

  const clearSearch = () => {
    setTerm("");
    doSearch("");
  };

  return (
    <section data-testid="section-search-bar">
      <InputField
        label=""
        className="!gap-0"
        data-testid="input-search-bar"
        placeholder="O que você está buscando?"
        leadingItem={{
          type: "icon",
          content: "action-search",
        }}
        onChange={handleSearch}
        size={size}
        value={term}
        trailingItem={
          term
            ? {
                type: "icon",
                content: "close",
              }
            : undefined
        }
        onTrailingItemClick={clearSearch}
      />
    </section>
  );
};
